import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import SOPLibrary from "@/pages/SOPLibrary";
import SOPBuilder from "@/pages/SOPBuilder";
import SOPDetail from "@/pages/SOPDetail";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Billing from "@/pages/Billing";
import ModuleDetail from "@/pages/ModuleDetail";
import RecordDetail from "@/pages/RecordDetail";
import Team from "@/pages/Team";
import ActivityFeed from "@/pages/ActivityFeed";
import PrintRecord from "@/pages/PrintRecord";
import Templates from "@/pages/Templates";
import AppTemplates from "@/pages/AppTemplates";
import Admin from "@/pages/Admin";
import Contact from "@/pages/Contact";
import { Privacy, Terms, Cookies } from "@/pages/Policies";
import PageViewTracker from "@/components/PageViewTracker";
import HR from "@/pages/HR";
import Marketing from "@/pages/Marketing";
import AIAssistant from "@/pages/AIAssistant";
import Modules from "@/pages/Modules";
import InkHub from "@/pages/ink/InkHub";
import InkCopilot from "@/pages/ink/InkCopilot";
import InkProducts from "@/pages/ink/InkProducts";
import InkPresses from "@/pages/ink/InkPresses";
import InkTroubleshoot from "@/pages/ink/InkTroubleshoot";
import InkReports from "@/pages/ink/InkReports";
import InkComingSoon from "@/pages/ink/InkComingSoon";
import Library from "@/pages/library/Library";
import LibraryResource from "@/pages/library/LibraryResource";
import MyLibrary from "@/pages/library/MyLibrary";
import ToolsHub from "@/pages/tools/ToolsHub";
import OEECalculator from "@/pages/tools/OEECalculator";
import FiveSAudit from "@/pages/tools/FiveSAudit";
import DowntimeCost from "@/pages/tools/DowntimeCost";
import CAPABuilder from "@/pages/tools/CAPABuilder";
import CycleTimeAnalyzer from "@/pages/tools/CycleTimeAnalyzer";
import ShiftHandover from "@/pages/tools/ShiftHandover";
import GmesHub from "@/pages/gmes/GmesHub";
import GmesMachines from "@/pages/gmes/GmesMachines";
import GmesWorkOrders from "@/pages/gmes/GmesWorkOrders";
import GmesOee from "@/pages/gmes/GmesOee";
import GmesDowntime from "@/pages/gmes/GmesDowntime";
import GmesProduction from "@/pages/gmes/GmesProduction";
import GmesShifts from "@/pages/gmes/GmesShifts";
import Enterprise from "@/pages/enterprise/Enterprise";
import Security from "@/pages/enterprise/Security";
import Investors from "@/pages/enterprise/Investors";
import About from "@/pages/enterprise/About";
import DashLayout from "@/components/DashLayout";

function Private({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-12 font-mono-plex text-sm">Loading RuleAI…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PageViewTracker />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/templates" element={<Templates />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/library" element={<Library />} />
          <Route path="/library/:slug" element={<LibraryResource />} />
          <Route path="/tools" element={<ToolsHub />} />
          <Route path="/tools/oee-calculator" element={<OEECalculator />} />
          <Route path="/tools/5s-audit" element={<FiveSAudit />} />
          <Route path="/tools/downtime-cost" element={<DowntimeCost />} />
          <Route path="/tools/capa-builder" element={<CAPABuilder />} />
          <Route path="/tools/cycle-time-analyzer" element={<CycleTimeAnalyzer />} />
          <Route path="/tools/shift-handover" element={<ShiftHandover />} />
          <Route path="/enterprise" element={<Enterprise />} />
          <Route path="/security" element={<Security />} />
          <Route path="/investors" element={<Investors />} />
          <Route path="/about" element={<About />} />
          <Route element={<Private><DashLayout /></Private>}>
            <Route path="/app" element={<Dashboard />} />
            <Route path="/app/modules" element={<Modules />} />
            <Route path="/app/sops" element={<SOPLibrary />} />
            <Route path="/app/sops/builder" element={<SOPBuilder />} />
            <Route path="/app/sops/:id" element={<SOPDetail />} />
            <Route path="/app/hr" element={<HR />} />
            <Route path="/app/marketing" element={<Marketing />} />
            <Route path="/app/assistant" element={<AIAssistant />} />
            <Route path="/app/billing" element={<Billing />} />
            <Route path="/app/modules/:moduleId" element={<ModuleDetail />} />
            <Route path="/app/records/:id" element={<RecordDetail />} />
            <Route path="/app/team" element={<Team />} />
            <Route path="/app/activity" element={<ActivityFeed />} />
            <Route path="/app/records/:id/print" element={<PrintRecord />} />
            <Route path="/app/templates" element={<AppTemplates />} />
            <Route path="/app/ink" element={<InkHub />} />
            <Route path="/app/ink/copilot" element={<InkCopilot />} />
            <Route path="/app/ink/products" element={<InkProducts />} />
            <Route path="/app/ink/presses" element={<InkPresses />} />
            <Route path="/app/ink/troubleshoot" element={<InkTroubleshoot />} />
            <Route path="/app/ink/reports" element={<InkReports />} />
            <Route path="/app/ink/coming-soon/:slug" element={<InkComingSoon />} />
            <Route path="/app/library" element={<MyLibrary />} />
            <Route path="/app/shopfloor" element={<GmesHub />} />
            <Route path="/app/shopfloor/machines" element={<GmesMachines />} />
            <Route path="/app/shopfloor/work-orders" element={<GmesWorkOrders />} />
            <Route path="/app/shopfloor/oee" element={<GmesOee />} />
            <Route path="/app/shopfloor/downtime" element={<GmesDowntime />} />
            <Route path="/app/shopfloor/production" element={<GmesProduction />} />
            <Route path="/app/shopfloor/shifts" element={<GmesShifts />} />
            <Route path="/app/admin" element={<Admin />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}
